import axios from 'axios';

const api = axios.create({
  baseURL: '/api'
});

// Add authentication token to requests
api.interceptors.request.use(config => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiration
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      if (localStorage.getItem('auth_token')) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Authentication
export const login = async (email, password) => {
  const response = await api.post('/auth/login', { email, password });
  if (response.data.token) {
    localStorage.setItem('auth_token', response.data.token);
    localStorage.setItem('user', JSON.stringify(response.data.user));
  }
  return response.data;
};

export const register = async (userData) => {
  const response = await api.post('/users', userData);
  return response.data;
};

export const getUserProfile = async () => {
  const response = await api.get('/users/profile');
  return response.data;
};

// Papers
export const fetchLatestIssue = async () => {
  try {
    const response = await api.get('/issues');
    // Return the first issue as latest
    return response.data[0] || null;
  } catch (error) {
    console.error('Error fetching latest issue:', error);
    return null;
  }
};

export const fetchRecentPapers = async () => {
  try {
    const response = await api.get('/papers');
    // Return up to 3 most recent papers
    return response.data;
  } catch (error) {
    console.error('Error fetching recent papers:', error);
    return [];
  }
};

export const fetchUserSubmissions = async () => {
  const response = await api.get('/papers/my-submissions/');
  return response.data;
};

export const submitPaper = async (paperData) => {
  const response = await api.post('/papers', paperData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export const searchPapers = async (params) => {
  const response = await api.get('/papers/search', { params });
  return response.data;
};

export const fetchPaperDetails = async (paperId) => {
  const response = await api.get(`/papers/${paperId}`);
  return response.data;
};

export const downloadPaper = async (paperId) => {
  const response = await api.get(`/papers/${paperId}/download`, {
    responseType: 'blob'
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `paper-${paperId}.pdf`);
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const withdrawPaper = async (paperId) => {
  const response = await api.post(`/papers/${paperId}/withdraw/`);
  return response.data;
};

export const editPaper = async (paperId, paperData) => {
  const response = await api.put(`/papers/${paperId}/`, paperData);
  return response.data;
};

export const getPaperForRevision = async (paperId) => {
  const response = await api.get(`/papers/${paperId}/revision/`);
  return response.data;
};

export const submitRevision = async (paperId, revisionData) => {
  const response = await api.post(`/papers/${paperId}/revision/`, revisionData);
  return response.data;
};

// Editor functions
export const fetchEditorSubmissions = async () => {
  const response = await api.get('/editor/submissions/');
  return response.data;
};

export const updatePaperStatus = async (paperId, status, comments) => {
  const response = await api.post(`/editor/papers/${paperId}/status/`, {
    status,
    comments
  });
  return response.data;
};

export const assignReviewers = async (paperId, reviewerIds) => {
  const response = await api.post(`/editor/papers/${paperId}/assign-reviewers/`, {
    reviewers: reviewerIds
  });
  return response.data;
};

// Reviews
export const submitReview = async (reviewData) => {
  const response = await api.post('/reviews/', reviewData);
  return response.data;
};

// Issues
export const fetchIssues = async () => {
  const response = await api.get('/issues');
  return response.data;
};

export const fetchAdminIssues = async () => {
  const response = await api.get('/admin/issues');
  return response.data;
};

export const fetchIssueDetails = async (issueId) => {
  const response = await api.get(`/issues/${issueId}`);
  return response.data;
};

// Categories
export const fetchCategories = async () => {
  const response = await api.get('/categories');
  return response.data;
};

// Admin functions
export const fetchAllUsers = async () => {
  const response = await api.get('/admin/users');
  return response.data;
};

export const updateUserRole = async (userId, role) => {
  const response = await api.put(`/admin/users/${userId}/role`, { role });
  return response.data;
};

export const createUser = async (userData) => {
  const response = await api.post('/admin/users', userData);
  return response.data;
};

export const updateUser = async (userId, userData) => {
  const response = await api.put(`/admin/users/${userId}`, userData);
  return response.data;
};

export const deleteUser = async (userId) => {
  const response = await api.delete(`/admin/users/${userId}`);
  return response.data;
};

export const fetchAllPapers = async () => {
  const response = await api.get('/admin/papers');
  return response.data;
};

export const createPaper = async (paperData) => {
  const formData = new FormData();
  Object.keys(paperData).forEach(key => {
    if (key === 'authors' && Array.isArray(paperData[key])) {
      formData.append(key, JSON.stringify(paperData[key]));
    } else {
      formData.append(key, paperData[key]);
    }
  });
  const response = await api.post('/admin/papers', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const updatePaper = async (paperId, paperData) => {
  const formData = new FormData();
  Object.keys(paperData).forEach(key => {
    if (key === 'authors' && Array.isArray(paperData[key])) {
      formData.append(key, JSON.stringify(paperData[key]));
    } else {
      formData.append(key, paperData[key]);
    }
  });
  const response = await api.put(`/admin/papers/${paperId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const deletePaper = async (paperId) => {
  const response = await api.delete(`/admin/papers/${paperId}`);
  return response.data;
};

export const createIssue = async (issueData) => {
  const response = await api.post('/admin/issues/', issueData);
  return response.data;
};

export const updateIssue = async (issueId, issueData) => {
  const response = await api.put(`/admin/issues/${issueId}/`, issueData);
  return response.data;
};

// Contact Forms and Newsletter
export const fetchContactForms = async () => {
  const response = await api.get('/admin/contact-forms');
  return response.data;
};

export const markContactFormAsRead = async (contactId) => {
  const response = await api.put(`/admin/contact-forms/${contactId}/read`);
  return response.data;
};

export const fetchNewsletterSubscriptions = async () => {
  const response = await api.get('/admin/newsletter-subscriptions');
  return response.data;
};

// Editorial Board Management
export const fetchEditorialBoard = async () => {
  const response = await api.get('/editorial-board');
  return response.data;
};

export const fetchAdminEditorialBoard = async () => {
  const response = await api.get('/admin/editorial-board');
  return response.data;
};

export const createEditorialBoardMember = async (memberData) => {
  const formData = new FormData();
  Object.keys(memberData).forEach(key => {
    formData.append(key, memberData[key]);
  });
  const response = await api.post('/admin/editorial-board', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const updateEditorialBoardMember = async (memberId, memberData) => {
  const formData = new FormData();
  Object.keys(memberData).forEach(key => {
    formData.append(key, memberData[key]);
  });
  const response = await api.put(`/admin/editorial-board/${memberId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const deleteEditorialBoardMember = async (memberId) => {
  const response = await api.delete(`/admin/editorial-board/${memberId}`);
  return response.data;
};

// Editorial Team Management
export const fetchEditorialTeam = async () => {
  const response = await api.get('/editorial-team');
  return response.data;
};

export const fetchAdminEditorialTeam = async () => {
  const response = await api.get('/admin/editorial-team');
  return response.data;
};

export const createEditorialTeamMember = async (memberData) => {
  const formData = new FormData();
  Object.keys(memberData).forEach(key => {
    formData.append(key, memberData[key]);
  });
  const response = await api.post('/admin/editorial-team', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const updateEditorialTeamMember = async (memberId, memberData) => {
  const formData = new FormData();
  Object.keys(memberData).forEach(key => {
    formData.append(key, memberData[key]);
  });
  const response = await api.put(`/admin/editorial-team/${memberId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const deleteEditorialTeamMember = async (memberId) => {
  const response = await api.delete(`/admin/editorial-team/${memberId}`);
  return response.data;
};

// Enhanced Issue Management with PDF upload
export const createIssueWithPdf = async (issueData) => {
  const formData = new FormData();
  Object.keys(issueData).forEach(key => {
    if (key === 'papers' && Array.isArray(issueData[key])) {
      formData.append(key, JSON.stringify(issueData[key]));
    } else {
      formData.append(key, issueData[key]);
    }
  });
  const response = await api.post('/admin/issues', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const updateIssueWithPdf = async (issueId, issueData) => {
  const formData = new FormData();
  Object.keys(issueData).forEach(key => {
    if (key === 'papers' && Array.isArray(issueData[key])) {
      formData.append(key, JSON.stringify(issueData[key]));
    } else {
      formData.append(key, issueData[key]);
    }
  });
  const response = await api.put(`/admin/issues/${issueId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const downloadIssuePdf = async (issueId) => {
  const response = await api.get(`/issues/${issueId}/download`, {
    responseType: 'blob'
  });
  return response.data;
};

// Announcements management
export const fetchAnnouncements = async () => {
  const response = await api.get('/announcements');
  return response.data;
};

export const createAnnouncement = async (announcementData) => {
  const formData = new FormData();
  Object.keys(announcementData).forEach(key => {
    if (key === 'attachments') {
      // Handle file array
      if (Array.isArray(announcementData[key])) {
        announcementData[key].forEach(file => formData.append('attachments', file));
      }
    } else {
      formData.append(key, announcementData[key]);
    }
  });

  const response = await api.post('/admin/announcements', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const updateAnnouncement = async (id, announcementData) => {
  const response = await api.put(`/admin/announcements/${id}`, announcementData);
  return response.data;
};

export const deleteAnnouncement = async (id) => {
  const response = await api.delete(`/admin/announcements/${id}`);
  return response.data;
};

export default api;